import Skeleton from "common/components/ui/Skeleton";
import useEstimateCreation from "common/datahooks/useEstimateCreation";
import useEstimateUpdate from "common/datahooks/useEstimateUpdate";
import { formatPrice } from "common/helpers/utils";
import useDebounce from "common/hooks/useDebounce";
import type {
  PricingPeriodUnit,
  Subscription,
  SubscriptionType,
} from "common/types";

interface ToPayNowProps {
  subscription: Subscription;
  type: SubscriptionType;
  newCount: number;
  currentCount: number;
  selectedPeriodUnit: PricingPeriodUnit;
}

export default function ToPayNow({
  subscription,
  type,
  selectedPeriodUnit,
  currentCount,
  newCount,
}: ToPayNowProps) {
  const [debouncedCount] = useDebounce(newCount);

  const isDowngradeOrEqual = newCount <= currentCount;
  const isInTrial = subscription?.status === "in_trial";

  const isPriceZero =
    selectedPeriodUnit === "month" && (isInTrial || isDowngradeOrEqual);

  const { estimateCreation, isLoadingEstimateCreation } = useEstimateCreation({
    subscription,
    type,
    selectedPeriodUnit,
    quantity: debouncedCount,
  });

  const { estimateUpdate, isLoadingEstimateUpdate } = useEstimateUpdate({
    subscription,
    type,
    selectedPeriodUnit,
    quantity: debouncedCount,
    prorate: true,
    isDisabled: isPriceZero,
  });

  const isLoading = isLoadingEstimateCreation || isLoadingEstimateUpdate;

  let price = 0;
  if (!isPriceZero && !isLoading) {
    price = estimateCreation
      ? estimateCreation.amountDue
      : estimateUpdate.amountDue;
  }

  return (
    <div className="flex items-center justify-between px-2 py-1">
      <span className="text-md-bold">To pay now</span>

      {isLoading && !isPriceZero ? (
        <Skeleton className="h-8 w-24" />
      ) : (
        <span className="text-2xl-bold text-purple-600">
          {formatPrice(price / 100)}
        </span>
      )}
    </div>
  );
}
